import { useQuery } from '@apollo/client';
import type { CommunityWorkflowConfiguration, WorkflowType } from '@communities/content-management/domain';
import { GET_COMMUNITY_WORKFLOWS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

interface CommunityWorkflowsResponse {
  community: {
    id: string;
    workflowConfigurations: CommunityWorkflowConfiguration[];
  } | null;
}

export function useCommunityWorkflows(communityId?: string, type?: WorkflowType) {
  const { data, loading, error, refetch } = useQuery<CommunityWorkflowsResponse>(GET_COMMUNITY_WORKFLOWS, {
    variables: { communityId },
    fetchPolicy: 'cache-first',
    skip: !communityId,
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const allWorkflows: CommunityWorkflowConfiguration[] = useMemo(() => {
    const rawWorkflows = data?.community?.workflowConfigurations || [];

    return (
      rawWorkflows
        .filter(workflow => workflow && workflow.type) // Filter out null/undefined workflows
        // eslint-disable-next-line complexity
        .map(workflow => ({
          type: workflow.type,
          topicActions: (workflow.topicActions || []).map(topicAction => ({
            topicName: topicAction?.topicName || '',
            actionNames: topicAction?.actionNames || [],
          })),
          apiSettings: {
            maxTps: workflow.apiSettings?.maxTps || 100,
            monthlyBudget: workflow.apiSettings?.monthlyBudget,
            costPerAudioHour: workflow.apiSettings?.costPerAudioHour,
            numberOfClipsPerHour: workflow.apiSettings?.numberOfClipsPerHour,
            accessClients: (workflow.apiSettings?.accessClients || []).map(client => ({
              providerUrl: client?.providerUrl || '',
              clientIdList: client?.clientIdList || [],
            })),
            ...(workflow.apiSettings?.contentTypeTps && { contentTypeTps: workflow.apiSettings.contentTypeTps }),
          },
        }))
    );
  }, [data]);

  const workflows: CommunityWorkflowConfiguration | null = type ? allWorkflows.find(wf => wf.type === type) || null : null;

  return {
    workflows,
    loading,
    errors,
    refetch,
  };
}

export default useCommunityWorkflows;
